if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      quantityInputSticky = undefined;
      quantityFormSticky = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input-primary');
        this.quantityInputSticky = this.querySelector('.quantity__input-sticky');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput && !this.quantityInputSticky) return;

        this.quantityForm = this.querySelector('.product-form__quantity-primary');
        this.quantityFormSticky = this.querySelector('.product-form__quantity-sticky');
        if (!this.quantityForm && !this.quantityFormSticky) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }


      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = (this.dataset.url !== productUrl) || this.querySelector('li[data-gang-option]') !== null;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects[data-primary]');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
        const variantSelectsAlt = html.querySelector('variant-selects[data-sticky]');
        if (variantSelectsAlt) {
          HTMLUpdateUtility.viewTransition(this.variantSelectorsAlt, variantSelectsAlt, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Price-Alt');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateCustomLiquid(html);
          
          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            theme.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateCustomLiquid(html) {
        this.querySelectorAll('.custom__liquid').forEach(block => {
          if (block.id) {
            const liquid = html.querySelector(`#${block.id}`);
            if (liquid) block.innerHTML = liquid.innerHTML;
          }
        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-${this.dataset.section}--alt`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, theme.variantStrings.unavailable);

        const selectors = ['price', 'Price-Alt', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      // Tag detection methods for quantity rules
      getProductTags(input) {
        // Helper method to get product tags from various sources
        let tags = [];
        
        // Try to get from data attribute on the element or its container
        const productContainer = input.closest("[data-product-tags]") || this.querySelector("[data-product-tags]");
        if (productContainer && productContainer.dataset.productTags) {
          tags = productContainer.dataset.productTags.split(',').map(tag => tag.trim());
        }
        
        // Try to get from global product object if available
        if (tags.length === 0 && typeof window.product !== "undefined" && window.product.tags) {
          tags = Array.isArray(window.product.tags) ? window.product.tags : window.product.tags.split(',').map(tag => tag.trim());
        }
        
        return tags;
      }

      isMin200Tag(input) {
        const tags = this.getProductTags(input);
        const hasMin200 = tags.includes('min200');
        console.log("Product-info.js - Checking for min200 tag:", tags, "has min200:", hasMin200);
        return hasMin200;
      }

      isMin300Tag(input) {
        const tags = this.getProductTags(input);
        const hasMin300 = tags.includes('min300');
        console.log("Product-info.js - Checking for min300 tag:", tags, "has min300:", hasMin300);
        return hasMin300;
      }

      isMin1000Tag(input) {
        const tags = this.getProductTags(input);
        const hasMin1000 = tags.includes('min1000');
        console.log("Product-info.js - Checking for min1000 tag:", tags, "has min1000:", hasMin1000);
        return hasMin1000;
      }

      isMin2000Tag(input) {
        const tags = this.getProductTags(input);
        const hasMin2000 = tags.includes('min2000');
        console.log("Product-info.js - Checking for min2000 tag:", tags, "has min2000:", hasMin2000);
        return hasMin2000;
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul') || this.querySelector('product-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`) || html.querySelector(`product-gallery ul`);

        const refreshSourceData = () => {
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        const mediaGallery = this.querySelector('media-gallery');
        if (mediaGallery != null) {
          mediaGallery.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);
        } else {
          this.querySelector('[id^="Slider-"]')?.scrollTo({ left: 0 });
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

setQuantityBoundries() {
        const inputs = [this.quantityInput, this.quantityInputSticky].filter(Boolean);
        inputs.forEach((input) => {
          if (!input) return;
          
          // Check for tag-based rules first (highest priority)
          const isMin2000Tag = this.isMin2000Tag(input);
          const isMin1000Tag = this.isMin1000Tag(input);
          const isMin200Tag = this.isMin200Tag(input);
          const isMin300Tag = this.isMin300Tag(input);
          
          // Check for product type to determine min and step values
          let minValue = 50; // Default
          let stepValue = 50; // Default
          
          // Tag-based rules take precedence
          if (isMin2000Tag) {
            minValue = 2000;
            stepValue = 100;
            console.log('Product-info.js - Using min2000 tag rules: min=2000, step=100');
          } else if (isMin1000Tag) {
            minValue = 1000;
            stepValue = 100;
            console.log('Product-info.js - Using min1000 tag rules: min=1000, step=100');
          } else if (isMin300Tag) {
            minValue = 300;
            stepValue = 100;
            console.log('Product-info.js - Using min300 tag rules: min=300, step=100');
          } else if (isMin200Tag) {
            minValue = 200;
            stepValue = 100;
            console.log('Product-info.js - Using min200 tag rules: min=200, step=100');
          } else {
            // Get product type from parent elements (fallback to product type rules)
            const productContainer = input.closest('[data-product-type]');
            if (productContainer) {
              const productType = (productContainer.dataset.productType || '').toLowerCase();
              console.log('Product type from container:', productType);
              
              // Set values based on product type
              if (productType.includes('e-invitation') || productType.includes('einvitation') || productType.includes('e invitation')) {
                minValue = 1;
                stepValue = 1;
              } else if (productType.includes('return gift') || productType.includes('return-gift') || 
                         productType.includes('returngift') || productType.includes('return gifts') || 
                         productType.includes('return-gifts') || productType.includes('returngifts') ||
                         productType.includes('return_gift') || productType.includes('return_gifts')) {
                minValue = 10;
                stepValue = 10;
              } else if (productType.includes('paper')) {
                minValue = 100;
                stepValue = 100;
              } else if (productType.includes('wedding card') || productType.includes('wedding-card') || 
                         productType.includes('weddingcard') || productType.includes('wedding cards') || 
                         productType.includes('wedding-cards') || productType.includes('weddingcards')) {
                minValue = 100;
                stepValue = 50;
              } else if (productType.includes('sale')) {
                minValue = 100;
                stepValue = 50;
              }
            }
          }
          
          // Use input's min/step if already set (from liquid template)
          if (input.hasAttribute('min')) {
            minValue = parseInt(input.getAttribute('min'), 10) || minValue;
          }
          
          if (input.hasAttribute('step')) {
            stepValue = parseInt(input.getAttribute('step'), 10) || stepValue;
          }
          
          const data = {
            cartQuantity: input.dataset.cartQuantity ? parseInt(input.dataset.cartQuantity) : 0,
            min: minValue,
            max: input.dataset.max ? parseInt(input.dataset.max) : null,
            step: stepValue,
          };
          
          console.log('Quantity input settings:', { min: minValue, step: stepValue, productType: input.closest('[data-product-type]')?.dataset.productType });
          
          let min = data.min;
          const max = data.max === null ? data.max : data.max - data.cartQuantity;
          if (max !== null) min = Math.min(min, max);
          
          input.min = min;
          input.step = data.step;
          if (max) input.max = max; else input.removeAttribute('max');
          
          // Set value to normalized multiple of step
          const current = parseInt(input.value, 10) || min;
          input.value = min + Math.round((current - min) / data.step) * data.step;
          
          // Minimum value enforcement for tagged products (tag rules take precedence)
          if (isMin2000Tag) {
            // Ensure min2000 tagged products start at 2000
            if (parseInt(input.value, 10) < 2000) {
              input.value = 2000;
              console.log('Enforcing minimum value of 2000 for min2000 tagged product');
            }
          } else if (isMin1000Tag) {
            // Ensure min1000 tagged products start at 1000
            if (parseInt(input.value, 10) < 1000) {
              input.value = 1000;
              console.log('Enforcing minimum value of 1000 for min1000 tagged product');
            }
          } else if (isMin300Tag) {
            // Ensure min300 tagged products start at 300
            if (parseInt(input.value, 10) < 300) {
              input.value = 300;
              console.log('Enforcing minimum value of 300 for min300 tagged product');
            }
          } else if (isMin200Tag) {
            // Ensure min200 tagged products start at 200
            if (parseInt(input.value, 10) < 200) {
              input.value = 200;
              console.log('Enforcing minimum value of 200 for min200 tagged product');
            }
          } else {
            // Special case for product type rules (fallback when no tags)
            const productContainer = input.closest('[data-product-type]');
            if (productContainer) {
              const productType = (productContainer.dataset.productType || '').toLowerCase();
              if (productType.includes('return gift') || productType.includes('return-gift') || 
                  productType.includes('returngift') || productType.includes('return gifts') || 
                  productType.includes('return-gifts') || productType.includes('returngifts') ||
                  productType.includes('return_gift') || productType.includes('return_gifts')) {
                // Ensure Return Gifts start at 10
                if (parseInt(input.value, 10) < 10) {
                  input.value = 10;
                  console.log('Enforcing minimum value of 10 for Return Gifts product');
                }
              } else if (productType.includes('paper')) {
                // Ensure Paper products start at 100
                if (parseInt(input.value, 10) < 100) {
                  input.value = 100;
                  console.log('Enforcing minimum value of 100 for Paper product');
                }
              } else if (productType.includes('wedding card') || productType.includes('wedding-card') || 
                         productType.includes('weddingcard') || productType.includes('wedding cards') || 
                         productType.includes('wedding-cards') || productType.includes('weddingcards')) {
                // Ensure Wedding Cards start at 100
                if (parseInt(input.value, 10) < 100) {
                  input.value = 100;
                  console.log('Enforcing minimum value of 100 for Wedding Cards product');
                }
              } else if (productType.includes('sale')) {
                // Ensure Sale products start at 100
                if (parseInt(input.value, 10) < 100) {
                  input.value = 100;
                  console.log('Enforcing minimum value of 100 for Sale product');
                }
              }
            }
          }
          
          // Restore from localStorage
          const productId = this.querySelector('[data-product-id]')?.dataset.productId || 'default';
          const saved = localStorage.getItem(`pdp-qty-${productId}`);
          if (saved) {
            input.value = Math.max(min, parseInt(saved, 10)); // Enforce min
          }
          
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        //this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            //this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput && !this.quantityInputSticky) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`) || html.getElementById(`Quantity-Form-${sectionId}-sticky`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm?.querySelector(selector);
          const currentSticky = this.quantityFormSticky?.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if(!updated) continue;
          if (!current && !currentSticky) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current?.setAttribute(attribute, valueUpdated);
                currentSticky?.setAttribute(attribute, valueUpdated);
              } else {
                current?.removeAttribute(attribute);
                currentSticky?.removeAttribute(attribute);
              }
            }
          } else {
            if(current) current.innerHTML = updated.innerHTML;
            if(currentSticky) currentSticky.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects[data-primary]');
      }

      get variantSelectorsAlt() {
        return this.querySelector('variant-selects[data-sticky]');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }


      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
